import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
    Leaf, ArrowLeft, GameController, Lock, Coins,
    Brain, Lightning, Shuffle, TreeStructure, MagnifyingGlass, LinkSimple
} from '@phosphor-icons/react';

const GAME_META = {
    memory_match: { icon: Brain, color: '#22c55e', description: 'Flip cards to find matching Australian animals!' },
    quiz_race: { icon: Lightning, color: '#f59e0b', description: 'Answer animal questions before time runs out!' },
    word_scramble: { icon: Shuffle, color: '#38bdf8', description: 'Unscramble letters to spell animal names!' },
    habitat_builder: { icon: TreeStructure, color: '#a855f7', description: 'Drag animals into the correct Australian habitat!' },
    spot_difference: { icon: MagnifyingGlass, color: '#ec4899', description: 'Find subtle differences in nature scenes!' },
    food_chain: { icon: LinkSimple, color: '#ef4444', description: 'Connect animals in the correct food chain order!' }
};

export default function GamesHubPage() {
    const { user, api } = useAuth();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api().get('/games/config');
                setConfig(res.data);
            } catch (err) {
                console.error('Failed to load games config');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const tokenProgress = config ? Math.min(100, (config.tokens_earned_today / config.daily_limit) * 100) : 0;

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">Games</span>
                        </Link>
                        <Link to="/dashboard" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5" data-testid="back-dashboard">
                            <ArrowLeft weight="bold" size={16} /> Dashboard
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="max-w-7xl mx-auto px-4 pt-24 pb-24">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b]">Games</h1>
                        <p className="text-[#475569] mt-1">Learn about Australian wildlife while having fun!</p>
                    </div>
                    {config && (
                        <div className="brutalist-card p-3 flex items-center gap-3 bg-white" data-testid="daily-tokens">
                            <Coins weight="duotone" size={24} className="text-[#f59e0b]" />
                            <div>
                                <p className="text-xs text-[#94a3b8] font-semibold">Today's Tokens</p>
                                <div className="flex items-center gap-2">
                                    <div className="w-24 h-2 bg-[#e2e8f0] rounded-full">
                                        <div className="h-full bg-[#f59e0b] rounded-full transition-all" style={{ width: `${tokenProgress}%` }} />
                                    </div>
                                    <span className="text-sm font-bold text-[#1e293b]">{config.tokens_earned_today}/{config.daily_limit}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {config?.games.map((game, i) => {
                        const meta = GAME_META[game.id] || {};
                        const Icon = meta.icon || GameController;
                        const isLocked = !game.unlocked;
                        const hasRoute = true; // All games built

                        return (
                            <motion.div
                                key={game.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className={`brutalist-card p-5 relative overflow-hidden ${isLocked ? 'opacity-60' : ''}`}
                                data-testid={`game-card-${game.id}`}
                            >
                                {isLocked && (
                                    <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-[#1e293b] text-white text-xs font-bold rounded-full">
                                        <Lock weight="bold" size={12} /> {game.unlock_tokens} Tokens
                                    </div>
                                )}
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                                    style={{ backgroundColor: `${meta.color}15` }}>
                                    <Icon weight="duotone" size={30} style={{ color: meta.color }} />
                                </div>
                                <h3 className="font-fredoka text-lg font-bold text-[#1e293b] mb-1">{game.name}</h3>
                                <p className="text-sm text-[#475569] mb-4">{meta.description}</p>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-[#94a3b8] font-semibold">Up to {game.max_tokens} tokens</span>
                                    {isLocked ? (
                                        <span className="brutalist-btn bg-[#94a3b8] text-white text-sm cursor-not-allowed flex items-center gap-1">
                                            <Lock weight="bold" size={14} /> Locked
                                        </span>
                                    ) : hasRoute ? (
                                        <Link to={`/games/${game.id.replace('_', '-')}`}
                                            className="brutalist-btn text-white text-sm flex items-center gap-1"
                                            style={{ backgroundColor: meta.color }}
                                            data-testid={`play-${game.id}`}
                                        >
                                            <GameController weight="bold" size={16} /> Play
                                        </Link>
                                    ) : (
                                        <span className="brutalist-btn bg-[#e2e8f0] text-[#94a3b8] text-sm cursor-not-allowed">
                                            Coming Soon
                                        </span>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
